<template>
	<div class="container-fluid px-4 pb-4">
		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<!-- One toolbar for the three things this surface lets an operator do:
		     add a station, stop drawing retired ones, and open or close the list
		     that doubles as the selection. -->
		<div class="d-flex flex-wrap align-items-center gap-3 mb-2">
			<button
				type="button"
				class="btn btn-primary btn-sm"
				:disabled="picking"
				@click="startPicking"
			>
				<i class="bi bi-plus-lg me-1"></i>Add station
			</button>

			<div class="form-check form-switch mb-0">
				<input
					id="stationShowRetired"
					v-model="showRetired"
					class="form-check-input"
					type="checkbox"
				/>
				<label class="form-check-label small" for="stationShowRetired">
					Show retired
				</label>
			</div>

			<span class="small text-muted">
				{{ listedStations.length }} of {{ stations.length }} station{{
					stations.length === 1 ? "" : "s"
				}}
			</span>

			<button
				type="button"
				class="btn btn-outline-secondary btn-sm ms-auto"
				:aria-expanded="panelOpen"
				@click="panelOpen = !panelOpen"
			>
				<i class="bi bi-layout-sidebar-reverse me-1"></i>
				{{ panelOpen ? "Hide list" : "Show list" }}
			</button>
		</div>

		<!-- Picking: the map's next click is a parcel, not a pan. -->
		<div
			v-if="picking"
			class="alert alert-info d-flex flex-wrap align-items-center gap-2 py-2"
			role="alert"
		>
			<i class="bi bi-crosshair"></i>
			<span>
				Click the parcel where the station goes. Pick a parcel at zoom level 14
				or closer; a point with no parcel on file is offered as well.
			</span>
			<button
				type="button"
				class="btn btn-outline-secondary btn-sm ms-auto"
				@click="cancelPicking"
			>
				Cancel
			</button>
		</div>

		<div class="stations-page">
			<div class="stations-map">
				<StationMap
					:show-retired="showRetired"
					:selected-station-id="selectedStationID"
					:selected-parcel-external-id="selectedParcelExternalID"
					:picking="picking"
					:fit-geometry="fitGeometry"
					@station-click="onStationClick"
					@parcel-click="onParcelClick"
				/>

				<div v-if="loading" class="stations-loading">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading…</span>
					</div>
					<p class="mt-2 mb-0 text-muted small">Loading stations…</p>
				</div>
			</div>

			<!-- The station list: the district's stations, newest id first, with
			     the two facts an operator scans for — whether the station is still
			     standing and how much trapping it has behind it. -->
			<aside v-if="panelOpen" class="stations-panel card">
				<div
					class="card-header d-flex justify-content-between align-items-center"
				>
					<span class="fw-semibold">Stations</span>
					<button
						type="button"
						class="btn-close btn-sm"
						aria-label="Hide the station list"
						@click="panelOpen = false"
					></button>
				</div>

				<div
					v-if="listedStations.length === 0"
					class="card-body text-muted small"
				>
					{{
						stations.length === 0
							? "This district has no stations yet. Use Add station to set the first one."
							: "No active stations. Show retired to see the district's retired stations."
					}}
				</div>

				<div v-else class="list-group list-group-flush">
					<button
						v-for="station in listedStations"
						:key="station.id"
						type="button"
						class="list-group-item list-group-item-action py-2"
						:class="{ active: station.id === selectedStationID }"
						@click="openStation(station)"
					>
						<div
							class="d-flex justify-content-between align-items-center gap-2"
						>
							<div class="text-truncate">
								<div class="fw-semibold text-truncate">
									{{ stationLabel(station.id, station.site) }}
								</div>
								<div class="small d-flex align-items-center gap-2 mt-1">
									<span
										class="badge rounded-pill"
										:class="station.active ? 'bg-success' : 'bg-secondary'"
									>
										{{ station.active ? "Active" : "Retired" }}
									</span>
									<span
										:class="
											station.id === selectedStationID
												? 'text-white-50'
												: 'text-muted'
										"
									>
										{{ station.instances }} instance{{
											station.instances === 1 ? "" : "s"
										}}
									</span>
								</div>
							</div>
							<i class="bi bi-chevron-right"></i>
						</div>
					</button>
				</div>
			</aside>
		</div>

		<StationPickerModal
			v-model="pickerOpen"
			:point="pickedPoint"
			:parcel="pickedParcel"
			@created="onStationCreated"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { GeoJSON } from "geojson";

import { apiClient } from "@/client";
import StationMap from "@/components/traps/StationMap.vue";
import StationPickerModal from "@/components/traps/StationPickerModal.vue";
import { stationLabel, type StationParcelClick } from "@/lib/stationMap";
import type { MapReviewTrapStationRow } from "@/type/api";

const route = useRoute();
const router = useRouter();

const stations = ref<MapReviewTrapStationRow[]>([]);
const loading = ref(false);
const error = ref("");

// Retired stations are drawn muted and can be hidden: the toggle is the map's
// filter and this list's filter, never a re-read.
const showRetired = ref(false);
const panelOpen = ref(true);
const selectedStationID = ref<number | null>(null);
const fitGeometry = ref<GeoJSON.GeoJSON | null>(null);

// Picking mode: the next map click is a parcel.
const picking = ref(false);
const pickerOpen = ref(false);
const pickedPoint = ref<{ latitude: number; longitude: number } | null>(null);
const pickedParcel = ref<{ external_id: string; region: string } | null>(null);
const selectedParcelExternalID = ref<string | null>(null);

/** The stations the list shows: newest id first, retired ones only when the
 * toggle asks for them. */
const listedStations = computed(() =>
	stations.value
		.filter((station) => showRetired.value || station.active)
		.sort((a, b) => b.id - a.id),
);

/** A point's frame: the station's own coordinates in a small box, so the camera
 * lands on the station rather than on a zero-area bounds a single point would
 * give it. */
function stationFitGeometry(
	row: MapReviewTrapStationRow,
): GeoJSON.GeoJSON | null {
	if (row.latitude === null || row.longitude === null) return null;
	const pad = 0.003;
	const [lng, lat] = [row.longitude, row.latitude];
	return {
		type: "Feature",
		properties: {},
		geometry: {
			type: "Polygon",
			coordinates: [
				[
					[lng - pad, lat - pad],
					[lng + pad, lat - pad],
					[lng + pad, lat + pad],
					[lng - pad, lat + pad],
					[lng - pad, lat - pad],
				],
			],
		},
	};
}

function selectStation(row: MapReviewTrapStationRow) {
	selectedStationID.value = row.id;
	fitGeometry.value = stationFitGeometry(row);
}

/** A row is both a selection and a way into the station's own page: it frames
 * the station on the map, then opens the review page the row names. */
function openStation(row: MapReviewTrapStationRow) {
	selectStation(row);
	void router.push(`/_/traps/stations/${row.id}`);
}

/** A station circle on the map: the tile names the station, and the row that
 * names it gives the page its way in. */
function onStationClick(id: number) {
	const row = stations.value.find((station) => station.id === id);
	if (row) {
		openStation(row);
		return;
	}
	void router.push(`/_/traps/stations/${id}`);
}

function startPicking() {
	picking.value = true;
}

function cancelPicking() {
	picking.value = false;
	selectedParcelExternalID.value = null;
}

/** A parcel click (or, in picking mode, a bare point): outline what was picked
 * and hand it to the dialog, which resolves the parcel and offers its sites.
 * Outside picking mode the map's parcels are only background, so a click on one
 * is not a step in any flow. */
function onParcelClick(hit: StationParcelClick) {
	if (!picking.value) return;
	pickedPoint.value = { latitude: hit.lat, longitude: hit.lng };
	pickedParcel.value = hit.external_id
		? { external_id: hit.external_id, region: hit.region }
		: null;
	selectedParcelExternalID.value = hit.external_id || null;
	pickerOpen.value = true;
}

function onStationCreated(id: number) {
	cancelPicking();
	void router.push(`/_/traps/stations/${id}`);
}

// Escape leaves picking mode. While the dialog is open it owns Escape (it
// closes itself), so this only answers when nothing else is in front of the
// map.
function onKeydown(event: KeyboardEvent) {
	if (event.key === "Escape" && picking.value && !pickerOpen.value) {
		cancelPicking();
	}
}

/** The district's stations, newest id first — the same collection the map's
 * tiles are cut from, read once for the list and its counts. */
async function load() {
	loading.value = true;
	error.value = "";
	try {
		const envelope =
			await apiClient.JSONQueryAll<MapReviewTrapStationRow>(
				"/api/trap-station",
			);
		stations.value = envelope.items;
	} catch (e: any) {
		error.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to load the district's stations.";
	} finally {
		loading.value = false;
	}
}

/** `?station=<id>` names the station to frame — the link the station's own page
 * offers as "Show on map". It selects the station and fits the camera to it;
 * an id the district does not hold is simply not selected. */
function selectRequestedStation() {
	const requested = Number(route.query.station);
	if (!Number.isFinite(requested)) return;
	const row = stations.value.find((station) => station.id === requested);
	if (row) selectStation(row);
}

onMounted(async () => {
	window.addEventListener("keydown", onKeydown);
	await load();
	selectRequestedStation();
});

onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown));
</script>

<style scoped>
/* The map fills the surface: the page's own remaining height, less the section
 * header and this view's toolbar, which the shell above it owns. */
.stations-page {
	position: relative;
	display: flex;
	height: calc(100vh - 280px);
	min-height: 420px;
	border-radius: 0.375rem;
	overflow: hidden;
}

.stations-map {
	position: relative;
	flex: 1 1 auto;
	min-width: 0;
	height: 100%;
}

.stations-loading {
	position: absolute;
	inset: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background: rgba(255, 255, 255, 0.65);
}

.stations-panel {
	flex: 0 0 22rem;
	max-width: 40%;
	overflow-y: auto;
	border-top-left-radius: 0;
	border-bottom-left-radius: 0;
}
</style>
